import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PullToRefresh from "react-simple-pull-to-refresh";

import {
  ContainerView,
  PullGrid,
  CorrectPullRefresh,
} from "components/containers";
import { TitleH1 } from "components/generic";
import HowItWork from "components/HowItWork";
import { LoaderCustom } from "components/Loader";
import { MenuLeftSide } from "components/MenuLeftSide";
import {
  IHostStripeMatches,
  IHouse,
  IMatchesEnum,
  IUserShot,
  TModeMatch,
} from "interfaces/intarfaces";
import { MainWrapper } from "pages/HostHouseRegistration/styles";
import { theme } from "styles/theme";
import { API_MATCHES, API_USERS, matchStatus } from "utils/constants";
import { UserAPI } from "API/userApi";
import { MatchCard } from "./MatchCard";
import { useContextHospi } from "context/ContextHospi";
import { useContextHost } from "context/ContextHost";
import { UnauthorizedUser } from "pages/ProfileUser/SimpleComponents";
import { HelpWidget } from "components/HelpWidget";
import { HostAPI } from "API/hostApi";

export const HostMatches: React.FC = () => {
  const { userProfile } = useContextHospi();
  const { housesShotData, getHousesShortData } = useContextHost();
  const { t } = useTranslation("matches");
  const [cardsList, setCardsList] = useState<IHostStripeMatches[]>([]);
  const [loading, setLoading] = useState(false);
  // Errors block
  const [response401, setResponse401] = useState(false);

  let isAllowed =
    userProfile?.is_profile_complete! &&
    userProfile.verification_status! === "verified";

  useEffect(() => {
    getHousesShortData()
  }, [])

  const handleMatchStatus = (
    match_data: Pick<IMatchesEnum, "house_id" | "student_id">,
    mode: TModeMatch,
    interval?: [string, string] | undefined
  ) => {
    setLoading(true);
    const modeHandle = {
      acceptInvite: API_MATCHES + "invite",
      rejectInvite: API_MATCHES + "cancel-invite",
      removeMatch: API_MATCHES + "remove-match",
    };
    let payload: IMatchesEnum = {
      ...match_data,
      host_id: userProfile?.user_id || "",
    };
    if (mode === "acceptInvite" && interval)
      payload = {
        ...payload,
        student_check_in_date: interval[0],
        student_check_out_date: interval[1],
      };
    HostAPI.post(modeHandle[mode], payload)
      .then((response) => {
        if (response.hasOwnProperty("status")) {
          setResponse401(response.status === 401);
        } else if (response.hasOwnProperty("success") && response.success) {
          if (mode === "removeMatch") {
            setCardsList((prev) =>
              prev.filter((item) => !(item.house.house_id === match_data.house_id && item.student.user_id === match_data.student_id))
            );
          }
          // Change offers status in state
          else {
            setCardsList((prev) =>
              prev.map((item) => {
                if (item.house.house_id === match_data.house_id)
                  return {
                    ...item,
                    experience: {
                      ...item.experience,
                      isInvited: mode === "acceptInvite",
                    },
                  };
                return item;
              })
            );
          }
        }
      })
      .catch()
      .finally(() => setLoading(false));
  };

  const fetchData = (isSubscribed: boolean) => {

    HostAPI.get(API_MATCHES + `host-matches/${userProfile?.user_id}`)
      .then((response) => {
        if (
          response.hasOwnProperty("status") ||
          response.hasOwnProperty("isAxiosError")
        ) {
          throw new Error("Something went wrong, 109");
        }
        // If we got success response
        else if (response.length) {

          let studentCache = new Map<string, IUserShot>();
          let houseCache = new Map<
            string,
            Pick<IHouse, "house_id" | "title">
          >();
          const arrayStudentID: string[] = [];
          const arrayHouseID: string[] = [];
          response.forEach((item: IMatchesEnum) => {
            !arrayStudentID.includes(item.student_id) &&
              arrayStudentID.push(item.student_id);
            !arrayHouseID.includes(item.house_id) &&
              arrayHouseID.push(item.house_id);
          });
          // Get data houses from Context
          if (housesShotData.length) {
            housesShotData.forEach((item) => {
              if (arrayHouseID.includes(item.house_id!))
                houseCache.set(
                  item.house_id!,
                  item as Pick<IHouse, "house_id" | "title">
                );
            });
          } else throw new Error("Something went wrong, 108");
          Promise.allSettled(
            arrayStudentID.map((item) => UserAPI.get(API_USERS + item + "/user-preview")
            )
          )
            .then((results: PromiseSettledResult<IUserShot>[]) => {
              results.forEach(
                (item: PromiseSettledResult<IUserShot>, num: number) => {
                  item.status === "fulfilled" &&
                    studentCache.set(
                      (item.value as IUserShot).user_id,
                      item.value as IUserShot
                    );
                }
              );
              let newCardList: IHostStripeMatches[] = [];
              response.forEach((item: IMatchesEnum) => {
                if (
                  studentCache.get(item.student_id) !== undefined &&
                  houseCache.get(item.house_id) !== undefined &&
                  [
                    matchStatus.INVITED,
                    matchStatus.CHATTED,
                    matchStatus.LIKED,
                  ].includes(item.match_status!)
                )
                  newCardList.push({
                    student: studentCache.get(item.student_id)!,
                    house: houseCache.get(item.house_id)!,
                    experience: {
                      offered_check_in_date: item.offered_check_in_date || null,
                      offered_check_out_date:
                        item.offered_check_out_date || null,
                      isChatted: item.match_status === matchStatus.CHATTED,
                      isInvited: [
                        matchStatus.INVITED,
                        matchStatus.ACEPTED,
                      ].includes(item.match_status!),
                    },
                  });
              });
              isSubscribed && setCardsList((prev) => [...newCardList]);
              isSubscribed && setLoading(false);
            })
            .catch(() => {
              if (isSubscribed) {
                setCardsList([]);
                setLoading(false);
              }
            });
        } else {
          isSubscribed && setLoading(false);
        }
      })
      .catch(() => {
        if (isSubscribed) {
          setCardsList([]);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    let isSubscribed = isAllowed;
    isSubscribed && setLoading(true);

    fetchData(isSubscribed);
    // eslint-disable-next-line
  }, [housesShotData]);

  const correctStyle = {
    gap: "1rem",
    paddingBottom: "1rem",
    justifySelf: "end",
    width: "100%",
  };

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  return (
    <ContainerView>
      <MenuLeftSide />
      <MainWrapper style={correctStyle}>
        {loading && (
          <LoaderCustom
            size={5}
            color={theme.colors.bgBody}
            text={t("loading")}
          />
        )}
        <CorrectPullRefresh>
          <PullToRefresh onRefresh={async () => fetchData(true)}>
            <PullGrid>
              <TitleH1>{t("host.title")}</TitleH1>
              <HowItWork
                title={t("butHowItWork")}
                items={[t("host.firstDesc"), t("host.secondDesc")]}
              />
              {!!cardsList.length &&
                cardsList.map((card, num) => (
                  <MatchCard
                    key={num}
                    {...card}
                    handleMatchStatus={handleMatchStatus}
                  />
                ))}
              <HelpWidget
                title={t("dashboard.textHelp.title")}
                text={t("dashboard.textHelp.content").split(".")}
                btnText={t("dashboard.buttonHelp")}
              />
            </PullGrid>
          </PullToRefresh>
        </CorrectPullRefresh>
      </MainWrapper>
    </ContainerView>
  );
};
